import { styled, alpha } from "@mui/material/styles";

import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableContainer from "@mui/material/TableContainer";
import Menu from "@mui/material/Menu";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";

import COLORS from "@styles/theme/colors";

interface IconWrap {
  color: string;
}

interface CellProps {
  clicable: boolean | undefined;
}

const clicableStyles = {
  cursor: "pointer",
  transition: "all .3s",
  "&:hover": {
    color: COLORS.accent,
  },
};

export const StyledTableHead = styled(TableHead)(({ theme }) => ({
  "& tr": {
    border: `1px solid ${COLORS.borderColor}`,

    "&:last-child": {
      "& th": {
        "&:first-of-type": {
          borderRadius: "14px 0 0 0",
        },

        "&:last-child": {
          borderRadius: "0 14px 0 0",
        },
      },
    },
  },

  "& th": {
    fontSize: 14,
    backgroundColor: "#FCFDFD",
    color: COLORS.textcolor,
    padding: theme.spacing(1, 1),
    borderBottom: "0.6px solid #D5D5D5",
    fontWeight: "500",

    "&:last-child": {
      position: "sticky",
      right: 0,
    },

    "&:nth-child(2)": {
      position: "sticky",
      left: 0,
      zIndex: 3,
    },

    "& .MuiTableSortLabel-root": {
      transition: "all.3s ease",

      "&:hover": {
        opacity: 0.8,
      },
    },
  },
}));

export const StyledTableBoldCell = styled("div")<CellProps>(
  ({ theme, clicable }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    ...(clicable && clicableStyles),
  })
);

export const StyledTableBody = styled(TableBody)(({ theme }) => ({
  "& tr": {
    position: "relative",

    "&:last-child": {
      "& td": {
        borderBottom: "0px solid transparent",
      },

      "& td": {
        "&:first-of-type": {
          borderRadius: "0 0 0 14px",
        },

        "&:last-child": {
          borderRadius: "0 0 14px 0",
        },
      },

      "&.error": {
        "& td": {
          "&:first-of-type": {
            borderRadius: "0 0 0 14px",
          },

          "&:last-child": {
            borderRadius: "0 0 14px 0",
          },
        },
      },
    },

    "& td": {
      "& input": {
        padding: 5,
        fontSize: 14,
        transition: "0.2s all ease",

        "&:focus": {
          border: "1px solid",
          borderColor: COLORS.accent,
          borderRadius: 4,
        },

        "&::placeholder": {
          color: "#FF0000",
          opacity: 1,
        },
      },
      "& .MuiInputBase-input": {
        fontSize: 14,
        color: COLORS.textColor,
      },
      "& *": {
        border: "none",
      },
    },

    "&.error": {
      "& td": {
        backgroundColor: COLORS.white,
        color: COLORS.textColor,
        borderBottom: "1px solid #FF0000",
        borderTop: "1px solid #FF0000",

        "&:first-of-type": {
          borderLeft: "1px solid #FF0000",
        },

        "&:last-child": {
          borderRight: "1px solid #FF0000",
        },
      },
    },

    "&.rose": {
      "& td": {
        backgroundColor: "#F0E8F5",
      },
    },

    "&.blue": {
      "& td": {
        backgroundColor: "#D2D9ED",
      },
    },
  },
  "& .MuiTableCell-root": {
    height: 50,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    backgroundColor: COLORS.white,
    color: COLORS.textColor,
    borderBottom: "0.6px solid #D5D5D5",
    padding: theme.spacing(1, 1),
    fontSize: 14,

    "&:last-child": {
      position: "sticky",
      right: 0,
    },

    "&:nth-child(2)": {
      position: "sticky",
      left: 0,
      zIndex: 3,
    },

    "& a": {
      color: COLORS.textColor,
      fontWeight: 600,
      fontSize: 14,
      textDecoration: "none",
      cursor: "pointer",
      transition: "0.4s all ease",

      "&:hover": {
        color: COLORS.accent,
      },
    },
  },
}));

export const TableInner = styled("div")({
  width: "100%",
});

export const TableWrapper = styled("div")({
  width: "100%",
});

export const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  border: "0.6px solid #D5D5D5",
  borderRadius: 14,

  "& .MuiTableRow-hover:hover": {
    backgroundColor: `${COLORS.ice} !important`,
    "&.error": {
      backgroundColor: `${COLORS.white} !important`,
    },
  },

  "&::after": {
    content: '""',
    position: "absolute",
    zIndex: -1,
    height: "calc(100% - 20px)",
    top: 10,
    right: -1,
    width: 3,
    background: "#666",
  },

  "&::-webkit-scrollbar": {
    display: "block",
    width: 2,
    height: 8,
  },

  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },

  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.primary.main,
    borderRight: "none",
    borderLeft: "none",
  },

  "&::-webkit-scrollbar-track-piece:end": {
    background: "transparent",
    marginBottom: 10,
  },

  "&::-webkit-scrollbar-track-piece:start": {
    background: "transparent",
    marginTop: 10,
  },
}));

export const TableFooter = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(1),
  position: "sticky",
  bottom: "10px",

  "& .MuiToolbar-root": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },

  "& .MuiTablePagination-displayedRows": {
    order: -1,
  },

  "& .MuiPagination-root": {
    order: -1,
    margin: "0 auto",
  },

  "& .MuiTablePagination-spacer": {
    display: "none",
  },

  "& MuiToolbar-root": {
    paddingLeft: 0,
  },

  "& .MuiTablePagination-toolbar": {
    paddingLeft: 0,
  },

  "& .MuiInputBase-root": {
    marginRight: 0,
  },

  "& .MuiTablePagination-select": {
    transition: "all .3s ease",
    borderColor: `${COLORS.snow} !important`,

    "&:hover": {
      borderColor: `${theme.palette.text.primary} !important`,
    },
  },
}));

export const DescriptionCell = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",

  "& button": {
    flex: "0 0 auto",
    marginLeft: 40,
  },

  "& p": {
    margin: 0,
    display: "-webkit-box",
    WebkitLineClamp: "2",
    WebkitBoxOrient: "vertical",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
});

export const IconWrap = styled("div")<IconWrap>(({ color }) => ({
  width: 29,
  backgroundColor: color,
  height: 29,
  borderRadius: 4,
  display: "flex",

  "& > *": {
    margin: "auto",
  },
}));

export const DropDownMenu = styled(Menu)({
  borderRadius: 4,
  boShadow: "0px 0px 22px rgba(28, 37, 44, 0.1)",
  marginTop: 5,
});

export const MenuList = styled(List)({
  borderBottom: `1px solid ${COLORS.grayLighter}`,
  borderTop: `1px solid ${COLORS.grayLighter}`,
});

export const MenuListItem = styled(ListItem)(({ theme }) => ({
  color: theme.palette.text.secondary,
  cursor: "pointer",

  "&:hover": {
    backgroundColor: COLORS.ice,
  },
}));

export const MenuIconWrapper = styled("div")({
  width: 18,
  height: 18,
  marginRight: 8,
  borderRadius: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#f5f6f9",
});

export const TextOverflowCell = styled("div")<CellProps>(({ clicable }) => ({
  maxWidth: 280,
  overflow: "auto",
  textOverflow: "ellipsis",
  ...(clicable && clicableStyles),
}));

export const ActionButtonsCell = styled("div")({
  display: "flex",
});
